// A tiny pixel renderer.
//
// - Sprites are arrays of strings plus a palette map (one character per pixel; '.' or ' ' is
//   transparent). They are baked once into an offscreen atlas and blitted with drawImage.
// - Scenes draw at their logical resolution into a small canvas that CSS scales up by an integer
//   factor with `image-rendering: pixelated`.
// - One shared ticker drives every scene at 12 fps with stepped frames. It stops while the tab is
//   hidden (and requestAnimationFrame does not run then anyway), and it records how long the
//   subscribers take per frame so the budget (< 2 ms) can be checked from window.__hashcurve.
import { debugSet } from '../lib/debug';

export type Grid = readonly string[];
export type Palette = Readonly<Record<string, string>>;

export const FPS = 12;
const FRAME_MS = 1000 / FPS;

const isClear = (c: string) => c === '.' || c === ' ';

/** Draw a grid into a context at (x, y), one fillRect per horizontal run of a colour. */
export function paintGrid(ctx: CanvasRenderingContext2D, grid: Grid, pal: Palette, x = 0, y = 0, px = 1) {
  for (let r = 0; r < grid.length; r++) {
    const row = grid[r]!;
    let c = 0;
    while (c < row.length) {
      const ch = row[c]!;
      if (isClear(ch)) {
        c++;
        continue;
      }
      let e = c + 1;
      while (e < row.length && row[e] === ch) e++;
      const color = pal[ch];
      if (color) {
        ctx.fillStyle = color;
        ctx.fillRect(x + c * px, y + r * px, (e - c) * px, px);
      }
      c = e;
    }
  }
}

export const gridSize = (g: Grid) => ({ w: Math.max(0, ...g.map((r) => r.length)), h: g.length });

/** Mirror a grid horizontally (for sprites that face the other way). */
export const mirror = (g: Grid): string[] => g.map((r) => [...r].reverse().join(''));

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Sprites baked once into one offscreen canvas (simple shelf packing). */
export class Atlas {
  readonly canvas: HTMLCanvasElement;
  private rects = new Map<string, Rect>();

  constructor(defs: Readonly<Record<string, Grid>>, pal: Palette, width = 256) {
    const entries = Object.entries(defs);
    let x = 0;
    let y = 0;
    let shelf = 0;
    const placed: [string, Grid, Rect][] = [];
    for (const [name, g] of entries) {
      const { w, h } = gridSize(g);
      if (x + w > width) {
        x = 0;
        y += shelf + 1;
        shelf = 0;
      }
      const r = { x, y, w, h };
      placed.push([name, g, r]);
      this.rects.set(name, r);
      x += w + 1;
      shelf = Math.max(shelf, h);
    }
    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = Math.max(1, y + shelf);
    const ctx = this.canvas.getContext('2d')!;
    for (const [, g, r] of placed) paintGrid(ctx, g, pal, r.x, r.y);
  }

  size(name: string): { w: number; h: number } {
    const r = this.rects.get(name);
    return r ? { w: r.w, h: r.h } : { w: 0, h: 0 };
  }

  draw(ctx: CanvasRenderingContext2D, name: string, x: number, y: number) {
    const r = this.rects.get(name);
    if (!r) return;
    ctx.drawImage(this.canvas, r.x, r.y, r.w, r.h, Math.round(x), Math.round(y), r.w, r.h);
  }
}

// ------------------------------------------------------------------ ticker
type Sub = (frame: number) => void;
const subs = new Set<Sub>();
let raf = 0;
let last = 0;
let frame = 0;
const stats = { fps: FPS, subscribers: 0, avgMs: 0, maxMs: 0, frames: 0 };

function loop(t: number) {
  raf = 0;
  if (typeof document !== 'undefined' && document.hidden) return; // resumes on visibilitychange
  if (t - last >= FRAME_MS - 2) {
    last = t;
    frame++;
    const t0 = performance.now();
    for (const s of subs) s(frame);
    const ms = performance.now() - t0;
    stats.frames++;
    stats.avgMs = stats.avgMs * 0.95 + ms * 0.05;
    stats.maxMs = Math.max(stats.maxMs * 0.995, ms);
  }
  if (subs.size) raf = requestAnimationFrame(loop);
}

function kick() {
  if (!raf && subs.size && !(typeof document !== 'undefined' && document.hidden)) raf = requestAnimationFrame(loop);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', kick);
  debugSet('pixel', stats);
}

/** Run `fn` on every stepped frame (12 fps) until the returned function is called. */
export function onFrame(fn: Sub): () => void {
  subs.add(fn);
  stats.subscribers = subs.size;
  kick();
  return () => {
    subs.delete(fn);
    stats.subscribers = subs.size;
    if (!subs.size && raf) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  };
}

// ------------------------------------------------------------------ helpers
/** Deterministic hash noise in [0, 1) for integer inputs. */
export function noise(a: number, b = 0, c = 0): number {
  let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

type RGB = [number, number, number];
const cache = new Map<string, RGB>();
export function rgb(hex: string): RGB {
  let v = cache.get(hex);
  if (!v) {
    const n = parseInt(hex.slice(1, 7), 16);
    v = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    cache.set(hex, v);
  }
  return v;
}
export const hex = ([r, g, b]: RGB) => `#${((1 << 24) | (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b)).toString(16).slice(1)}`;
export function mix(a: string, b: string, t: number): string {
  const x = rgb(a);
  const y = rgb(b);
  const k = Math.min(1, Math.max(0, t));
  return hex([x[0] + (y[0] - x[0]) * k, x[1] + (y[1] - x[1]) * k, x[2] + (y[2] - x[2]) * k]);
}

/** Bresenham line of 1-px squares. */
export function line(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, w = 1) {
  x0 = Math.round(x0);
  y0 = Math.round(y0);
  x1 = Math.round(x1);
  y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  const o = Math.floor(w / 2);
  for (;;) {
    ctx.fillRect(x0 - o, y0 - o, w, w);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x0 += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y0 += sy;
    }
  }
}

/** 50% checker dither of a rectangle (the pixel-art stand-in for transparency). */
export function dither(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, phase = 0) {
  for (let j = 0; j < h; j++) for (let i = (j + phase) & 1; i < w; i += 2) ctx.fillRect(x + i, y + j, 1, 1);
}
